import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Shield, Award, Users, Calendar, ArrowRight } from 'lucide-react';
import { mockEvents, mockAnnouncements } from '../utils/mockData';

const Home = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const features = [
    {
      icon: Shield,
      title: t('home.features.feature1.title'),
      description: t('home.features.feature1.description')
    },
    {
      icon: Award,
      title: t('home.features.feature2.title'),
      description: t('home.features.feature2.description')
    },
    {
      icon: Users,
      title: t('home.features.feature3.title'),
      description: t('home.features.feature3.description')
    },
    {
      icon: Calendar,
      title: t('home.features.feature4.title'),
      description: t('home.features.feature4.description')
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-32">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1920&h=1080&fit=crop" 
            alt="Hero" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-4 animate-fade-in">
              {t('home.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-red-400 mb-6 font-semibold">
              {t('home.hero.subtitle')}
            </p>
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              {t('home.hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/portal/register"
                className="px-8 py-4 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {t('home.hero.joinButton')}
              </Link>
              <Link
                to="/about"
                className="px-8 py-4 bg-white text-gray-900 rounded-md hover:bg-gray-100 transition-all font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {t('home.hero.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            {t('home.features.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-red-500 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <feature.icon className="text-red-600" size={28} />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Events Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {t('home.latestEvents')}
            </h2>
            <Link
              to="/events"
              className="flex items-center text-red-600 hover:text-red-700 font-semibold transition-colors"
            >
              {t('home.viewAll')}
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockEvents.slice(0, 3).map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <img
                  src={event.coverImage}
                  alt={event[`title${currentLang.charAt(0).toUpperCase() + currentLang.slice(1)}`]}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {event[`title${currentLang.charAt(0).toUpperCase() + currentLang.slice(1)}`]}
                  </h3>
                  <p className="text-gray-600 mb-4">{new Date(event.date).toLocaleDateString()}</p>
                  <Link
                    to="/events"
                    className="text-red-600 hover:text-red-700 font-medium inline-flex items-center"
                  >
                    {t('events.viewGallery')}
                    <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Announcements Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            {t('home.latestAnnouncements')}
          </h2>
          <div className="max-w-4xl mx-auto space-y-6">
            {mockAnnouncements.slice(0, 3).map((announcement) => (
              <div
                key={announcement.id}
                className="p-6 bg-red-50 border-l-4 border-red-600 rounded-r-lg hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {announcement[`title${currentLang.charAt(0).toUpperCase() + currentLang.slice(1)}`]}
                  </h3>
                  <span className="text-sm text-gray-600">
                    {new Date(announcement.date).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-gray-700 leading-relaxed">{announcement.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
